import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, MoreHorizontal, Plus, SlidersHorizontal, Trash2 } from "lucide-react";
import { useCalendarMutations, useCategories, useUpdateSettings } from "../api/hooks";
import type { Calendar, ViewName } from "../api/types";
import { CALENDAR_COLORS, HOLIDAY_COLOR } from "../lib/colors";
import { MONTHS_LONG, WEEKDAYS_LONG, addDays, parts, rangeTitle, weekday } from "../lib/date";
import { VIEW_LABELS, VIEW_ORDER } from "../lib/views";
import { anchorOf, useUi, type Anchor } from "../state/ui";
import { MiniCalendar } from "./MiniCalendar";
import { Menu } from "./ui/Menu";
import { Popover } from "./ui/Popover";
import { ColorPicker } from "./ui/ColorPicker";
import "./CalendarPage.css";

/** Headline for the visible period: "Outubro 2026", "12 – 18 out.", "Quarta, 14". */
function periodTitle(view: ViewName, date: string, days: string[]): { title: string; label: string } {
  const { y, m, d } = parts(date);
  const month = MONTHS_LONG[m - 1].replace(/^./, (c) => c.toUpperCase());
  if (view === "day") return { title: `${WEEKDAYS_LONG[weekday(date)].split("-")[0].replace(/^./, (c) => c.toUpperCase())}, ${d}`, label: `${month} ${y}` };
  if (view === "year") return { title: String(y), label: "Ano" };
  if (view === "month") return { title: month, label: String(y) };
  if (view === "schedule") return { title: "Próximos dias", label: rangeTitle(date, addDays(date, 30)) };
  const first = parts(days[0]);
  const last = parts(days[days.length - 1]);
  return {
    title: first.m === last.m ? `${first.d} – ${last.d}` : `${first.d}/${first.m} – ${last.d}/${last.m}`,
    label: rangeTitle(days[0], days[days.length - 1])
  };
}

/** One calendar in the "Agendas" panel: visibility, rename, color, delete. */
function CalendarRow({ calendar }: { calendar: Calendar }) {
  const { update, remove } = useCalendarMutations();
  const ui = useUi();
  const [menu, setMenu] = useState<Anchor | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(calendar.name);

  return (
    <li className="cal-row">
      {renaming ? (
        <form
          className="cal-rename"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) update.mutate({ id: calendar.id, name: name.trim() });
            setRenaming(false);
          }}
        >
          <input className="field" autoFocus value={name} onChange={(e) => setName(e.target.value)} onBlur={() => setRenaming(false)} aria-label="Nome da agenda" />
        </form>
      ) : (
        <label className="checkbox cal-check" style={{ ["--cal" as string]: calendar.color }}>
          <input type="checkbox" checked={calendar.isVisible} onChange={(e) => update.mutate({ id: calendar.id, isVisible: e.target.checked })} />
          <span className="cal-name">{calendar.name}</span>
        </label>
      )}
      <button className="icon-btn sm" aria-label={`Opções de ${calendar.name}`} onClick={(e) => setMenu(anchorOf(e.currentTarget))}>
        <MoreHorizontal size={16} />
      </button>
      {menu && (
        <Popover anchor={menu} placement="below" onClose={() => setMenu(null)} width={240} keepOpen>
          <div style={{ padding: 6 }}>
            <button className="menu-item" onClick={() => { ui.calendars.forEach((c) => update.mutate({ id: c.id, isVisible: c.id === calendar.id })); setMenu(null); }}>
              Exibir somente esta
            </button>
            <button className="menu-item" onClick={() => { setRenaming(true); setMenu(null); }}>
              Renomear
            </button>
            {!calendar.isDefault && (
              <button
                className="menu-item btn danger"
                onClick={() => {
                  setMenu(null);
                  if (window.confirm(`Excluir a agenda "${calendar.name}" e todos os seus eventos?`)) remove.mutate(calendar.id);
                }}
              >
                <Trash2 size={16} /> Excluir agenda
              </button>
            )}
            <div className="menu-sep" />
            <ColorPicker colors={CALENDAR_COLORS.map((hex) => ({ name: hex, hex }))} value={calendar.color} onChange={(hex) => hex && update.mutate({ id: calendar.id, color: hex })} />
          </div>
        </Popover>
      )}
    </li>
  );
}

function CalendarsPanel() {
  const ui = useUi();
  const { create } = useCalendarMutations();
  const updateSettings = useUpdateSettings();
  return (
    <div className="cal-panel">
      <div className="eyebrow cal-panel-label">Minhas agendas</div>
      <ul className="cal-list">
        {ui.calendars.map((c) => (
          <CalendarRow key={c.id} calendar={c} />
        ))}
      </ul>
      <button
        className="cal-add"
        onClick={() => {
          const name = window.prompt("Nome da nova agenda");
          if (name?.trim()) create.mutate({ name: name.trim(), color: CALENDAR_COLORS[(ui.calendars.length * 3) % CALENDAR_COLORS.length] });
        }}
      >
        <Plus size={16} /> Nova agenda
      </button>
      <div className="eyebrow cal-panel-label">Outras</div>
      <ul className="cal-list">
        <li className="cal-row">
          <label className="checkbox cal-check" style={{ ["--cal" as string]: HOLIDAY_COLOR }}>
            <input type="checkbox" checked={ui.me.settings.showHolidays} onChange={(e) => updateSettings.mutate({ showHolidays: e.target.checked })} />
            <span className="cal-name">Feriados no Brasil</span>
          </label>
        </li>
      </ul>
    </div>
  );
}

/** Calendar toggles as pills; the "Agendas" pill opens the full panel. */
function CalendarFilters() {
  const ui = useUi();
  const { update } = useCalendarMutations();
  const updateSettings = useUpdateSettings();
  const [panel, setPanel] = useState<Anchor | null>(null);
  return (
    <div className="cal-filters" role="group" aria-label="Agendas visíveis">
      {ui.calendars.map((c) => (
        <button
          key={c.id}
          className={`pill cal-pill${c.isVisible ? "" : " off"}`}
          aria-pressed={c.isVisible}
          onClick={() => update.mutate({ id: c.id, isVisible: !c.isVisible })}
          title={c.isVisible ? `Ocultar ${c.name}` : `Mostrar ${c.name}`}
        >
          <span className="color-dot" style={{ background: c.isVisible ? c.color : "transparent", boxShadow: `inset 0 0 0 1.5px ${c.color}` }} />
          {c.name}
        </button>
      ))}
      <button
        className={`pill cal-pill${ui.me.settings.showHolidays ? "" : " off"}`}
        aria-pressed={ui.me.settings.showHolidays}
        onClick={() => updateSettings.mutate({ showHolidays: !ui.me.settings.showHolidays })}
      >
        <span className="color-dot" style={{ background: ui.me.settings.showHolidays ? HOLIDAY_COLOR : "transparent", boxShadow: `inset 0 0 0 1.5px ${HOLIDAY_COLOR}` }} />
        Feriados
      </button>
      <button className="icon-btn sm" aria-label="Gerenciar agendas" title="Gerenciar agendas" onClick={(e) => setPanel(anchorOf(e.currentTarget))}>
        <SlidersHorizontal size={16} />
      </button>
      <CategoryPills />
      {panel && (
        <Popover anchor={panel} placement="below" onClose={() => setPanel(null)} width={280} keepOpen>
          <CalendarsPanel />
        </Popover>
      )}
    </div>
  );
}

/**
 * Category pills, after the calendar pills: "Todas" plus one per category the user's events
 * use (new ones appear on their own). Tapping a category shows only it; tapping others adds
 * them; tapping a selected one removes it; "Todas" clears. A selected key with no events
 * left stays listed so it can be turned off.
 */
function CategoryPills() {
  const ui = useUi();
  const { data = [] } = useCategories();
  const selected = ui.categoryFilter;
  const known = new Set(data.map((c) => c.key));
  const options = [...data, ...selected.filter((k) => !known.has(k)).map((key) => ({ key, label: key, emoji: "🏷️", count: 0 }))];
  if (!options.length) return null;
  const active = selected.length > 0;
  const toggle = (key: string) => ui.setCategoryFilter(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  return (
    <>
      <span className="cal-filters-sep" aria-hidden />
      <div className="cat-pills" role="group" aria-label="Filtrar por categoria">
        <button className={`pill cat-pill${active ? "" : " on"}`} aria-pressed={!active} onClick={() => ui.setCategoryFilter([])} title="Mostrar todas as categorias">
          Todas
        </button>
        {options.map((c) => {
          const on = selected.includes(c.key);
          return (
            <button
              key={c.key}
              className={`pill cat-pill${on ? " on" : active ? " off" : ""}`}
              aria-pressed={on}
              onClick={() => toggle(c.key)}
              title={on ? `Remover ${c.label} do filtro` : active ? `Mostrar também ${c.label}` : `Mostrar só ${c.label}`}
            >
              <span aria-hidden>{c.emoji}</span>
              {c.label}
            </button>
          );
        })}
      </div>
    </>
  );
}

/** Editorial header: period headline (opens a date picker), navigation, view switch, calendar filters. */
function CalendarHeader() {
  const ui = useUi();
  const [picker, setPicker] = useState<Anchor | null>(null);
  const [viewMenu, setViewMenu] = useState<Anchor | null>(null);
  const { title, label } = periodTitle(ui.view, ui.date, ui.visibleDays);

  return (
    <header className="cal-head">
      <div className="cal-head-row">
        <div className="cal-head-title">
          <span className="eyebrow">{label}</span>
          <button className="cal-title display" onClick={(e) => setPicker(anchorOf(e.currentTarget))} aria-haspopup="dialog" aria-label={`${title}, escolher data`}>
            {title}
            <ChevronDown size={20} className="cal-title-caret" />
          </button>
        </div>
        <div className="cal-head-nav">
          <button className="pill" onClick={ui.goToday}>
            Hoje
          </button>
          <button className="icon-btn" aria-label="Período anterior" onClick={() => ui.step(-1)}>
            <ChevronLeft size={20} />
          </button>
          <button className="icon-btn" aria-label="Próximo período" onClick={() => ui.step(1)}>
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div className="cal-head-row second">
        <CalendarFilters />
        <div className="view-switch" role="tablist" aria-label="Visualização">
          {VIEW_ORDER.map((v) => (
            <button key={v} role="tab" aria-selected={ui.view === v} className={`view-opt${ui.view === v ? " on" : ""}`} onClick={() => ui.setView(v)} title={`${VIEW_LABELS[v].label} (${VIEW_LABELS[v].key})`}>
              {VIEW_LABELS[v].label}
            </button>
          ))}
        </div>
        <button className="pill view-switch-compact" onClick={(e) => setViewMenu(anchorOf(e.currentTarget))} aria-haspopup="menu">
          {VIEW_LABELS[ui.view].label} <ChevronDown size={14} />
        </button>
      </div>

      {picker && (
        <Popover anchor={picker} placement="below" onClose={() => setPicker(null)} width={264} keepOpen>
          <MiniCalendar
            compact
            value={ui.date}
            today={ui.today}
            weekStart={ui.me.settings.weekStart}
            highlight={ui.view === "week" || ui.view === "4days" || ui.view === "day" ? ui.visibleDays : []}
            onSelect={(d) => {
              ui.setDate(d);
              if (ui.view === "year" || ui.view === "schedule") ui.setView("day");
              setPicker(null);
            }}
          />
        </Popover>
      )}
      {viewMenu && (
        <Menu
          anchor={viewMenu}
          onClose={() => setViewMenu(null)}
          items={VIEW_ORDER.map((v) => ({ key: v, label: VIEW_LABELS[v].label, active: ui.view === v, onSelect: () => ui.setView(v) }))}
        />
      )}
    </header>
  );
}

export function CalendarPage({ children }: { children: ReactNode }) {
  return (
    <div className="cal-page">
      <CalendarHeader />
      <div className="cal-body">{children}</div>
    </div>
  );
}
